/**
 * UploadStalenessBanner - tell an administrator when prices stopped arriving.
 *
 * Commodity prices reach Farmgate only when somebody uploads a spreadsheet.
 * There is no job behind them, unlike FX. In September 2026 that pipeline had
 * been silent since 15 June: the weekly digest skipped every account for three
 * months and price alerts had nothing to fire on, while every page went on
 * showing June as the newest figure. Nothing said a word.
 *
 * So this lives in the layout rather than on one page. It has to be visible
 * wherever an administrator happens to be, because the failure was precisely
 * that nobody went looking.
 *
 * It deliberately does NOT go on the Dashboard, which would have been the
 * obvious home: ProtectedRoute sends admins from /app/dashboard to
 * /admin/users, so an admin never sees that page at all.
 *
 * Two ages are shown because they answer different questions. The observation
 * age is what customers see and what the digest keys off. The upload age says
 * whether anybody is still sending anything: a recent upload with an old
 * observation date means somebody is working from a stale source, which is a
 * different conversation from nobody uploading.
 */

import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Button, IconButton, Collapse } from '@mui/material';
import { Icon } from '@iconify/react';
import { useAuth } from '../context/AuthContext';
import { useThemeMode } from '../context/ThemeContext';
import { useUploadFreshness } from '../services/queries';
import { fmtDate } from '../utils/format';

const DISMISS_KEY = 'farmgate-upload-staleness-dismissed';

// Two missed weekly cycles, matching UPLOAD_STALE_DAYS in the watchdog job.
// One slow week is not news; two is.
const STALE_AFTER_DAYS = 14;

function UploadStalenessBanner() {
  const { isAdmin } = useAuth();
  const { palette } = useThemeMode();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  // Only an administrator can act on this, and only they should see it.
  const { data } = useUploadFreshness({ enabled: isAdmin });

  if (!isAdmin || dismissed || !data) return null;

  const obsDays = data.daysSinceObservation;
  if (obsDays == null || obsDays <= STALE_AFTER_DAYS) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* private browsing */
    }
  };

  // An upload cannot carry observations newer than itself, so the upload age
  // is always the smaller of the two. Branching on which is larger was
  // therefore branching on something that never varies, and the common case,
  // nobody has uploaded at all, fell into the wrong arm and told the admin
  // the survey dates were old when they were simply absent.
  //
  // What actually distinguishes the two situations is the GAP between them.
  const uploadDays = data.daysSinceUpload;
  const lag = uploadDays == null ? null : obsDays - uploadDays;
  const uploadClause =
    uploadDays == null
      ? 'No spreadsheet has ever been uploaded.'
      : lag >= 7
        ? `The last spreadsheet landed ${uploadDays} days ago, carrying survey dates ${lag} days older still.`
        : `The last spreadsheet landed ${uploadDays} days ago and nothing has arrived since.`;

  return (
    <Collapse in>
      <Box
        role="status"
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
          px: 2.25, py: 1.5, mb: 2.5, borderRadius: '12px',
          bgcolor: palette.warningBg,
          border: `1px solid ${palette.warning}33`,
        }}
      >
        <Icon icon="ph:clock-countdown" width={18} height={18} color={palette.warning} />
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: palette.text }}>
            No new prices in {obsDays} days
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: palette.textSecondary, mt: 0.15, lineHeight: 1.5 }}>
            The newest price anyone can see is from {fmtDate(data.latestObsDate)}. {uploadClause}{' '}
            While this lasts the weekly digest sends nothing and price alerts cannot fire.
          </Typography>
        </Box>
        <Button
          component={RouterLink}
          to="/admin/upload"
          variant="contained"
          size="small"
          disableElevation
          sx={{
            bgcolor: palette.warning, color: '#fff', fontWeight: 600,
            textTransform: 'none', borderRadius: '9px', whiteSpace: 'nowrap',
            '&:hover': { bgcolor: palette.warning, filter: 'brightness(0.92)' },
          }}
        >
          Upload prices
        </Button>
        <IconButton
          size="small"
          onClick={dismiss}
          aria-label="Dismiss until next sign-in"
          sx={{ color: palette.textMuted }}
        >
          <Icon icon="ph:x" width={15} height={15} />
        </IconButton>
      </Box>
    </Collapse>
  );
}

export default UploadStalenessBanner;
